import {
  abortSignResponseSchema,
  aesPhoneVerificationLimitErrorResponseSchema,
  aesPhoneVerificationThrottleErrorResponseSchema,
  badRequestResponseSchema,
  confirmAesPhoneRequestSchema,
  confirmAesPhoneResponseSchema,
  generatePhoneVerificationResponse,
  getSignersResponseSchema,
  getSigningResponseSchema,
  kycRedirectResponseSchema,
  getSigningSignableSchema,
  nameRepresentativesRequestSchema,
  nameRepresentativesResponseSchema,
  notFoundResponseSchema,
  permissionDeniedResponseSchema,
  signingActionNotAllowedResponseSchema,
  signingNotFoundResponseSchema,
  signingSignCompleteResponseSchema,
  signingSignResponseSchema,
  signingSignStartResponseSchema,
  signRequestSchema,
  successResponseSchema,
  unauthorizedResponseSchema,
  unprocessableEntityResponseSchema,
  verifyAccessResponseSchema,
  verifyAccessSigningRequestBodySchema,
  verifyAccessUnprocessableEntityResponseSchema,
  webidStartRequestSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

const atrustStartResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ authorizeUrl: z.string() }),
});

const atrustCallbackRequestSchema = z.object({
  workflowId: z.string(),
  code: z.string(),
});

const atrustCallbackResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    step: z.enum(['credential', 'complete']),
    authorizeUrl: z.string().optional(),
  }),
});

// Shared provider error shape ({ success:false, code, message }) returned by the
// A-Trust and WebID sign routes.
const providerErrorSchema = z.object({
  success: z.literal(false),
  code: z.string(),
  message: z.string(),
});

const webidStatusResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ webidVerified: z.boolean() }),
});

// Extend request objects only for the frontend with necessary path params
const CRUD = {
  a: verifyAccessSigningRequestBodySchema.extend({ workflowId: z.string() }),
  s: z.object({ workflowId: z.string() }),
  r: z.object({ workflowId: z.string() }),
  u: signRequestSchema.extend({ workflowId: z.string() }),
  v: confirmAesPhoneRequestSchema.extend({ workflowId: z.string() }),
  w: webidStartRequestSchema.extend({ workflowId: z.string() }),
  n: nameRepresentativesRequestSchema.extend({ workflowId: z.string() }),
};

const endpoints = {
  'sign.abort': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/abort`,
    requestSchema: CRUD.r,
    responseSuccessSchema: abortSignResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof abortSignResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'sign.complete': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign-complete`,
    requestSchema: CRUD.r,
    responseSuccessSchema: signingSignCompleteResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof signingSignCompleteResponseSchema,
    z.ZodUndefined
  >,
  'sign.get': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/sign`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSigningResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSigningResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'sign.otp.send': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/aes-pin`,
    requestSchema: CRUD.r,
    responseSuccessSchema: successResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      422: unprocessableEntityResponseSchema,
      425: aesPhoneVerificationThrottleErrorResponseSchema,
      429: aesPhoneVerificationLimitErrorResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof successResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      422: typeof unprocessableEntityResponseSchema;
      425: typeof aesPhoneVerificationThrottleErrorResponseSchema;
      429: typeof aesPhoneVerificationLimitErrorResponseSchema;
    }
  >,
  'sign.otp.verify': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/aes-pin/verify`,
    requestSchema: CRUD.v,
    responseSuccessSchema: confirmAesPhoneResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.v,
    typeof confirmAesPhoneResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'sign.signable': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/signable-info`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSigningSignableSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSigningSignableSchema,
    z.ZodUndefined
  >,
  'sign.signers.get': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/sign/signers`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSignersResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSignersResponseSchema,
    {
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'sign.start.get': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/sign/start`,
    requestSchema: CRUD.r,
    responseSuccessSchema: signingSignResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof signingSignResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'sign.representatives.submit': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/representatives`,
    requestSchema: CRUD.n,
    responseSuccessSchema: nameRepresentativesResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: signingActionNotAllowedResponseSchema,
      404: signingNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.n,
    typeof nameRepresentativesResponseSchema,
    {
      400: typeof signingActionNotAllowedResponseSchema;
      404: typeof signingNotFoundResponseSchema;
    }
  >,
  'sign.start.post': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/start`,
    requestSchema: CRUD.u,
    responseSuccessSchema: signingSignStartResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: signingActionNotAllowedResponseSchema,
      404: signingNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.u,
    typeof signingSignStartResponseSchema,
    {
      400: typeof signingActionNotAllowedResponseSchema;
      404: typeof signingNotFoundResponseSchema;
    }
  >,
  'sign.verify': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/security/verify-access`,
    requestSchema: CRUD.a,
    responseSuccessSchema: verifyAccessResponseSchema,
    responseErrorSchemas: {
      404: signingNotFoundResponseSchema,
      403: permissionDeniedResponseSchema,
      422: verifyAccessUnprocessableEntityResponseSchema,
    },
    requiresToken: 'session',
  } satisfies EndpointDefinition<
    typeof CRUD.a,
    typeof verifyAccessResponseSchema,
    {
      404: typeof signingNotFoundResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      422: typeof verifyAccessUnprocessableEntityResponseSchema;
    }
  >,
  'sign.sms.verify.start': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/security/start-verification`,
    requestSchema: CRUD.s,
    responseSuccessSchema: generatePhoneVerificationResponse,
    responseErrorSchemas: {
      404: signingNotFoundResponseSchema,
      403: permissionDeniedResponseSchema,
      422: verifyAccessUnprocessableEntityResponseSchema,
    },
    requiresToken: 'session',
  } satisfies EndpointDefinition<
    typeof CRUD.s,
    typeof generatePhoneVerificationResponse,
    {
      404: typeof signingNotFoundResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      422: typeof verifyAccessUnprocessableEntityResponseSchema;
    }
  >,
  'sign.atrust.start': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/atrust/start`,
    requestSchema: CRUD.r,
    responseSuccessSchema: atrustStartResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: providerErrorSchema,
      404: signingNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof atrustStartResponseSchema,
    {
      400: typeof providerErrorSchema;
      404: typeof signingNotFoundResponseSchema;
    }
  >,
  'sign.atrust.callback': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/atrust/callback`,
    requestSchema: atrustCallbackRequestSchema,
    responseSuccessSchema: atrustCallbackResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: providerErrorSchema,
      404: signingNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof atrustCallbackRequestSchema,
    typeof atrustCallbackResponseSchema,
    {
      400: typeof providerErrorSchema;
      404: typeof signingNotFoundResponseSchema;
    }
  >,
  'sign.webid.start': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/sign/webid/start`,
    requestSchema: CRUD.w,
    responseSuccessSchema: kycRedirectResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: providerErrorSchema,
      404: providerErrorSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.w,
    typeof kycRedirectResponseSchema,
    {
      400: typeof providerErrorSchema;
      404: typeof providerErrorSchema;
    }
  >,
  'sign.webid.status': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/sign/webid/status`,
    requestSchema: CRUD.r,
    responseSuccessSchema: webidStatusResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      404: signingNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof webidStatusResponseSchema,
    {
      404: typeof signingNotFoundResponseSchema;
    }
  >,
};

export default endpoints;
